<template>
  <Transition name="banner">
    <div v-if="isVisible" class="recovery-banner" role="alert">
      <div class="recovery-banner__content">
        <span class="recovery-banner__icon">&#127908;</span>
        <span class="recovery-banner__message">
          {{ t('recording.recovery.banner', sessionCount) }}
        </span>
      </div>
      <div class="recovery-banner__actions">
        <button type="button" class="recovery-banner__action" @click="handleView">
          {{ t('recording.recovery.bannerAction') }}
        </button>
        <button
          type="button"
          class="recovery-banner__close"
          :aria-label="t('common.close')"
          @click="handleDismiss"
        >
          &#10005;
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  sessionCount: number
  isDismissed?: boolean
}

interface Emits {
  (e: 'view'): void
  (e: 'dismiss'): void
}

const props = withDefaults(defineProps<Props>(), {
  isDismissed: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

const isVisible = computed(() => {
  return props.sessionCount > 0 && !props.isDismissed
})

function handleView() {
  emit('view')
}

function handleDismiss() {
  emit('dismiss')
}
</script>

<style scoped>
.recovery-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-semantic-warning-background);
  border-bottom: 1px solid var(--color-semantic-warning);
  color: var(--color-text-primary);
}

.recovery-banner__content {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.recovery-banner__icon {
  font-size: var(--font-size-lg);
}

.recovery-banner__message {
  font-size: var(--font-size-sm);
}

.recovery-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.recovery-banner__action {
  background: transparent;
  border: none;
  color: var(--color-brand-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-base);
  transition: background var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.recovery-banner__action:hover {
  background: var(--color-overlay-hover);
}

.recovery-banner__close {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--radius-base);
  line-height: 1;
  min-width: var(--sizing-touch-target-min);
  min-height: var(--sizing-touch-target-min);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.recovery-banner__close:hover {
  background: var(--color-overlay-hover);
}

/* Transition animations */
.banner-enter-active,
.banner-leave-active {
  transition:
    opacity var(--transition-duration-normal) var(--transition-easing-ease-out),
    transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
